import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../utils/api';
import {
  Tenant,
  CreateTenant,
  UpdateTenant,
  TenantAction,
  TenantListResponse,
} from '../dtos/tenant';
import { accessToken } from '../utils/helper';
import { ApiResponseError } from '../dtos/common';

const getApiErrorMessage = (error: unknown) => {
  const apiError = error as ApiResponseError;
  return apiError.response?.data?.message || 'Something Went Wrong!';
};

interface TenantState {
  tenantList: TenantListResponse;
  selectedTenant: Tenant | null;
  tenantActions: TenantAction[];
  loading: boolean;
  actionsLoading: boolean;
  error: string | null;
  lastIssuedApiKey: { tenantId: string; apiKey: string } | null;
}

const initialState: TenantState = {
  tenantList: { data: [], total: 0, currentPage: 1, totalPages: 0 },
  selectedTenant: null,
  tenantActions: [],
  loading: false,
  actionsLoading: false,
  error: null,
  lastIssuedApiKey: null,
};

export const getTenantsTrigger = createAsyncThunk(
  'get/tenants',
  async (
    {
      parentCompanyId,
      page,
      limit,
      search,
      status,
      sortField,
      sortOrder,
    }: {
      parentCompanyId?: number;
      page?: number;
      limit?: number;
      search?: string;
      status?: string;
      sortField?: string;
      sortOrder?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const queryParams = new URLSearchParams();
      if (parentCompanyId) queryParams.append('parentCompanyId', parentCompanyId.toString());
      if (page) queryParams.append('page', page.toString());
      if (limit) queryParams.append('limit', limit.toString());
      if (search) queryParams.append('search', search);
      if (status) queryParams.append('status', status);
      if (sortField) queryParams.append('sortField', sortField);
      if (sortOrder) queryParams.append('sortOrder', sortOrder);

      const response = await api.get(`/tenants?${queryParams}`, accessToken());
      return response.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error));
    }
  },
);

export const createTenantTrigger = createAsyncThunk(
  'post/tenants',
  async (payload: CreateTenant, { rejectWithValue }) => {
    try {
      const response = await api.post<{ message: string; data: Tenant; apiKey: string }>(
        '/tenants',
        payload,
        accessToken(),
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error));
    }
  },
);

export const updateTenantTrigger = createAsyncThunk(
  'patch/tenants',
  async (
    { tenantId, data }: { tenantId: string; data: UpdateTenant },
    { rejectWithValue },
  ) => {
    try {
      const response = await api.patch<{ message: string; data: Tenant }>(
        `/tenants/${encodeURIComponent(tenantId)}`,
        data,
        accessToken(),
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error));
    }
  },
);

export const regenerateTenantApiKeyTrigger = createAsyncThunk(
  'patch/tenants/regenerate-api-key',
  async (tenantId: string, { rejectWithValue }) => {
    try {
      const response = await api.patch<{ message: string; apiKey: string }>(
        `/tenants/${encodeURIComponent(tenantId)}/regenerate-api-key`,
        {},
        accessToken(),
      );
      return { tenantId, apiKey: response.data.apiKey, message: response.data.message };
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error));
    }
  },
);

export const getTenantActionsTrigger = createAsyncThunk(
  'get/tenants/actions',
  async (tenantId: string, { rejectWithValue }) => {
    try {
      const response = await api.get<{ status: number; data: TenantAction[] }>(
        `/tenants/${encodeURIComponent(tenantId)}/actions`,
        accessToken(),
      );
      return response.data.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error));
    }
  },
);

export const createTenantActionTrigger = createAsyncThunk(
  'post/tenants/actions',
  async (
    { tenantId, action }: { tenantId: string; action: Partial<TenantAction> },
    { rejectWithValue },
  ) => {
    try {
      const response = await api.post<{ message: string; data: TenantAction }>(
        `/tenants/${encodeURIComponent(tenantId)}/actions`,
        action,
        accessToken(),
      );
      return response.data.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error));
    }
  },
);

export const updateTenantActionTrigger = createAsyncThunk(
  'patch/tenants/actions',
  async (
    {
      tenantId,
      actionId,
      action,
    }: {
      tenantId: string;
      actionId: number;
      action: Partial<TenantAction>;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await api.patch<{ message: string; data: TenantAction }>(
        `/tenants/${encodeURIComponent(tenantId)}/actions/${actionId}`,
        action,
        accessToken(),
      );
      return response.data.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error));
    }
  },
);

export const deleteTenantActionTrigger = createAsyncThunk(
  'delete/tenants/actions',
  async (
    { tenantId, actionId }: { tenantId: string; actionId: number },
    { rejectWithValue },
  ) => {
    try {
      await api.delete(
        `/tenants/${encodeURIComponent(tenantId)}/actions/${actionId}`,
        accessToken(),
      );
      return actionId;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error));
    }
  },
);

const tenantSlice = createSlice({
  name: 'tenant',
  initialState,
  reducers: {
    setSelectedTenant: (state, action) => {
      state.selectedTenant = action.payload;
    },
    clearLastIssuedApiKey: (state) => {
      state.lastIssuedApiKey = null;
    },
    clearTenantActions: (state) => {
      state.tenantActions = [];
    },
  },
  extraReducers: (builder) => {
    // getTenants
    builder.addCase(getTenantsTrigger.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(getTenantsTrigger.fulfilled, (state, action) => {
      state.loading = false;
      state.tenantList = action.payload;
    });
    builder.addCase(getTenantsTrigger.rejected, (state, action) => {
      state.loading = false;
      state.error = (action.payload as string) || 'Failed to fetch tenants';
    });

    // createTenant
    builder.addCase(createTenantTrigger.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(createTenantTrigger.fulfilled, (state, action) => {
      state.loading = false;
      const created = action.payload.data;
      state.lastIssuedApiKey = {
        tenantId: created.tenantId,
        apiKey: action.payload.apiKey,
      };
      state.tenantList.data = [created, ...state.tenantList.data];
      state.tenantList.total += 1;
    });
    builder.addCase(createTenantTrigger.rejected, (state, action) => {
      state.loading = false;
      state.error = (action.payload as string) || 'Failed to create tenant';
    });

    // updateTenant
    builder.addCase(updateTenantTrigger.fulfilled, (state, action) => {
      const updated = action.payload.data;
      state.tenantList.data = state.tenantList.data.map((item) =>
        item.tenantId === updated.tenantId ? updated : item,
      );
      if (state.selectedTenant?.tenantId === updated.tenantId) {
        state.selectedTenant = updated;
      }
    });

    // regenerateApiKey
    builder.addCase(regenerateTenantApiKeyTrigger.fulfilled, (state, action) => {
      state.lastIssuedApiKey = {
        tenantId: action.payload.tenantId,
        apiKey: action.payload.apiKey,
      };
      state.tenantList.data = state.tenantList.data.map((item) =>
        item.tenantId === action.payload.tenantId
          ? { ...item, apiKey: action.payload.apiKey }
          : item,
      );
    });

    // getTenantActions
    builder.addCase(getTenantActionsTrigger.pending, (state) => {
      state.actionsLoading = true;
    });
    builder.addCase(getTenantActionsTrigger.fulfilled, (state, action) => {
      state.actionsLoading = false;
      state.tenantActions = action.payload;
    });
    builder.addCase(getTenantActionsTrigger.rejected, (state) => {
      state.actionsLoading = false;
      state.tenantActions = [];
    });

    // createTenantAction
    builder.addCase(createTenantActionTrigger.fulfilled, (state, action) => {
      state.tenantActions = [action.payload, ...state.tenantActions];
    });

    // updateTenantAction
    builder.addCase(updateTenantActionTrigger.fulfilled, (state, action) => {
      state.tenantActions = state.tenantActions.map((item) =>
        item.id === action.payload.id ? action.payload : item,
      );
    });

    // deleteTenantAction
    builder.addCase(deleteTenantActionTrigger.fulfilled, (state, action) => {
      state.tenantActions = state.tenantActions.filter((item) => item.id !== action.payload);
    });
  },
});

export const { setSelectedTenant, clearLastIssuedApiKey, clearTenantActions } =
  tenantSlice.actions;
export default tenantSlice.reducer;
